import { AbilityDTO, AbilitySchema } from '@hexcrawl/shared-utils';
import { requestJson } from './api';

// Cache of abilities by ID
const abilityCache = new Map<string, AbilityDTO>();

// In-flight requests to avoid duplicate fetches
const pendingRequests = new Map<string, Promise<AbilityDTO>>();

/**
 * Get an ability by ID. Returns from cache if available, otherwise fetches from server.
 */
export async function getAbility(id: string): Promise<AbilityDTO> {
  // Check cache first
  const cached = abilityCache.get(id);
  if (cached) {
    return cached;
  }

  // Check if there's already a pending request for this ability
  const pending = pendingRequests.get(id);
  if (pending) {
    return pending;
  }

  // Fetch from server
  const request = requestJson<AbilityDTO>({
    url: `/game/ability?id=${encodeURIComponent(id)}`,
    method: 'GET',
    schema: AbilitySchema,
  }).then((ability) => {
    abilityCache.set(id, ability);
    pendingRequests.delete(id);
    return ability;
  }).catch((error) => {
    pendingRequests.delete(id);
    throw error;
  });

  pendingRequests.set(id, request);
  return request;
}

/**
 * Get an ability from cache only (synchronous). Returns undefined if not cached.
 */
export function getAbilityFromCache(id: string): AbilityDTO | undefined {
  return abilityCache.get(id);
}

/**
 * Preload abilities into the cache.
 */
export async function preloadAbilities(ids: string[]): Promise<void> {
  await Promise.all(ids.map(id => getAbility(id)));
}

/**
 * Clear the ability cache.
 */
export function clearAbilityCache(): void {
  abilityCache.clear();
}

export const abilityService = {
  getAbility,
  getAbilityFromCache,
  preloadAbilities,
  clearAbilityCache,
};
